<template>
  <div class="space-center login">
    <h2 style="font-weight: 900; margin-bottom: 1.3rem">{{ $t('register.title') }}</h2>

    <div class="form">
      <!-- 手机号输入 -->
      <div class="input-group" :class="{ error: accountError }">
        <input
          required="true"
          :placeholder="$t('register.phonePlaceholder')"
          class="main-input"
          type="text"
          v-model="form.account"
        />
        <div class="svg">
          <svg-icon :name="'用户'" color="svg"></svg-icon>
        </div>
      </div>
      <!-- 密码输入 -->
      <div class="input-group" style="margin-top: 1.5rem" :class="{ error: passwordError }">
        <input
          required="true"
          :placeholder="$t('register.passwordPlaceholder')"
          class="main-input shake-line"
          :type="showPassword ? 'text' : 'password'"
          v-model="form.password"
          @blur="validatePassword"
        />
        <div class="svg password-toggle" @click="showPassword = !showPassword">
          <svg-icon :name="showPassword ? '解锁' : '密码'" color="svg"></svg-icon>
        </div>
      </div>

      <!-- 确认密码 -->
      <div class="input-group" style="margin-top: 1.5rem" :class="{ error: confirmError }">
        <input
          required="true"
          :placeholder="$t('register.confirmPlaceholder')"
          class="main-input shake-line"
          :type="showConfirm ? 'text' : 'password'"
          v-model="form.confirm"
          @blur="validateConfirm"
        />
        <div class="svg password-toggle" @click="showConfirm = !showConfirm">
          <svg-icon :name="showConfirm ? '解锁' : '密码'" color="svg"></svg-icon>
        </div>
      </div>

      <!-- 验证码 -->
      <div style="display: flex; justify-content: space-between; margin-top: 1.5rem" class="input-group">
        <input
          style="width: 60%"
          required="true"
          maxLength="6"
          :placeholder="$t('common.verificationCodePlaceholder')"
          class="main-input"
          type="text"
          v-model="form.verifyCode"
        />
        <div class="Verify-Btn" :class="{ disabled: !isPhoneValid }" @click="getCode">
          {{ count > 0 ? count + 's' : $t('common.getVerificationCode') }}
        </div>
      </div>

      <!-- 协议勾选 -->
      <div class="group-password">
        <label class="agree-checkbox">
          <input type="checkbox" v-model="agree" />
          <span class="checkmark"></span>
          <span class="label-text"
            >{{ $t('common.agreement') }} <span class="link" @click.stop="showAgreement">{{ $t('common.userAgreement') }}</span>
            {{ $t('common.and') }}
            <span class="link" @click.stop="showPrivacy">{{ $t('common.privacyPolicy') }}</span>
          </span>
        </label>
      </div>

      <!-- 注册按钮 -->
      <button class="Signup-Btn" style="margin-top: 30px" @click="handleRegister">
        <svg-icon v-if="registerBtn == 0" size="18" :name="'用户'" color="#fff" />
        <svg-icon class="icon-loading" v-if="registerBtn == 1" size="18" :name="'加载'" color="#fff" />
        <transition name="fade">
          <svg-icon v-if="registerBtn == 2" size="18" :name="'成功'" color="#fff" />
        </transition>
        <span style="margin-left: 10px">
          {{ getRegisterTitle() }}
          <!-- // registerBtn === 1 // ? $t('register.registering') // : registerBtn === 2 // ? $t('register.registerSuccess') // :
          $t('register.registerButton') -->
        </span>
      </button>
    </div>
  </div>
</template>
<script setup lang="ts">
  import { ref, computed, onUnmounted } from 'vue';
  import { useRouter } from 'vue-router';
  import { toast } from 'd3components/src/utils/tools';
  import { useI18n } from 'vue-i18n';
  import { sendCode } from '@/utils/auth';
  defineOptions({ name: 'register' });

  const router = useRouter();
  const { t } = useI18n({ useScope: 'global' });
  const toastCenter = { top: '50vh' };
  const showPassword = ref(false);
  const showConfirm = ref(false);
  // ===== 表单数据 =====
  const form = ref({
    account: '',
    password: '',
    confirm: '',
    verifyCode: '',
  });
  const agree = ref(false);
  const getRegisterTitle = () => {
    switch (registerBtn.value) {
      case 1:
        return t('register.registering');
      case 2:
        return t('register.registerSuccess');
      default:
        return t('register.registerButton');
    }
  };

  // ===== 错误状态 =====
  const accountError = ref('');
  const passwordError = ref('');
  const confirmError = ref('');

  // ===== 注册按钮状态 =====
  const registerBtn = ref(0);

  // ===== 验证码计时器 =====
  const count = ref(0);
  let timer: any = null;

  // ===== 手机号验证 =====
  const isPhoneValid = computed(() => {
    const phoneReg = /^1[3-9]\d{9}$/;
    return phoneReg.test(form.value.account);
  });

  // ===== 表单验证 =====
  const validateAccount = () => {
    if (!form.value.account) {
      accountError.value = t('common.phoneRequired');
      // toast(t('common.phoneRequired'), 3, toastCenter);
      return false;
    }
    accountError.value = '';
    return true;
  };

  const validatePassword = () => {
    const p = form.value.password;
    if (!p) {
      passwordError.value = t('common.passwordRequired');
      // toast(t('common.passwordRequired'), 3, toastCenter);
      return false;
    }
    if (p.length < 2 || p.length > 20) {
      passwordError.value = t('common.passwordLength');
      // toast(t('common.passwordLength'), 3, toastCenter);

      return false;
    }
    passwordError.value = '';
    return true;
  };

  const validateConfirm = () => {
    if (!form.value.confirm) {
      confirmError.value = t('common.confirmPasswordRequired');
      // toast(t('common.confirmPasswordRequired'), 3, toastCenter);
      return false;
    }
    if (form.value.confirm !== form.value.password) {
      confirmError.value = t('common.passwordNotMatch');
      // toast(t('common.passwordNotMatch'), 3, toastCenter);
      return false;
    }
    confirmError.value = '';
    return true;
  };

  // ===== 表单整体验证 =====
  const isFormValid = computed(() => {
    return (
      form.value.account &&
      form.value.password &&
      form.value.confirm &&
      !accountError.value &&
      !passwordError.value &&
      !confirmError.value &&
      agree.value
    );
  });

  // ===== 获取验证码 =====
  const getCode = async () => {
    if (!isPhoneValid.value) {
      // toast(t('common.phoneRequired'), 3, toastCenter);
      return;
    }
    if (count.value > 0) return;

    try {
      const res = await sendCode(form.value.account);
      if (res && res.code === 0) {
        count.value = 60;
        timer = setInterval(() => {
          count.value--;
          if (count.value <= 0) {
            clearInterval(timer);
            timer = null;
          }
        }, 1000);
        // toast(t('register.codeSent'), 1, toastCenter);
        if ((res as any).mockCode) {
          form.value.verifyCode = (res as any).mockCode;
        }
      } else {
        // toast(res?.msg || t('register.sendFailed'), 3, toastCenter);
      }
    } catch (e: any) {
      // toast(e?.message || t('register.sendFailed'), 3, toastCenter);
    }
  };

  // ===== 注册提交 =====
  const handleRegister = async () => {
    if (!validateAccount() || !validatePassword() || !validateConfirm()) {
      return;
    }
    if (!form.value.verifyCode) {
      // toast(t('common.verificationCodeRequired'), 3, toastCenter);
      return;
    }
    if (!agree.value) {
      // toast(t('common.agreementRequired'), 3, toastCenter);
      return;
    }

    registerBtn.value = 1;

    // TODO: 调用注册API
    setTimeout(() => {
      registerBtn.value = 2;
      // toast(t('register.registerSuccess'), 1, toastCenter);
      setTimeout(() => {
        router.replace('/login');
      }, 1000);
    }, 1500);
  };

  // 打开协议链接
  const showAgreement = () => window.open('/protocol_cn.html', '_blank');
  const showPrivacy = () => window.open('/privacypolicy_cn.html', '_blank');

  const emit = defineEmits(['change-mode']);

  defineExpose({
    hasPrivacyConsent: () => agree.value,
  });
  const gotoLogin = () => emit('change-mode', 1);

  onUnmounted(() => {
    timer && clearInterval(timer);
  });
</script>

<style scoped lang="scss">
  // ===== 科技感配色 =====
  // $primary-gradient: linear-gradient(135deg, rgba(57, 140, 254, 1) 0%, rgba(26, 111, 220, 1) 100%);
  // $accent-color: rgba(57, 140, 254, 1);
  $primary-gradient: var(--color-primary);
  $accent-color: var(--color-primary);
  $light-accent: rgba(57, 140, 254, 0.1);
  $text-secondary: #8a94a6;
  $border-light: rgba(57, 140, 254, 0.2);

  // ===== 公共尺寸变量 =====
  $spacing-sm: 0.75rem; // 12px
  $spacing-md: 1.25rem; // 20px
  $spacing-lg: 1.5rem; // 24px
  $spacing-xl: 1.75rem; // 28px

  $radius-input: 54px;
  $radius-btn: 60px;
  $radius-full: 44px;
  $radius-sm: 4px;

  $shadow-sm: 0 2px 8px rgba(0, 0, 0, 0.02);
  $shadow-md: 0 4px 16px rgba($accent-color, 0.08);
  $shadow-focus:
    0 0 0 4px rgba($accent-color, 0.1),
    0 6px 20px rgba($accent-color, 0.12);
  $shadow-btn: 0 4px 16px rgba($accent-color, 0.3);
  $shadow-btn-hover: 0 6px 20px rgba($accent-color, 0.45);
  $shadow-btn-active: 0 2px 10px rgba($accent-color, 0.3);
  $shadow-verify: 0 2px 6px rgba($accent-color, 0.25);
  $shadow-verify-hover: 0 3px 10px rgba($accent-color, 0.35);

  $font-weight-normal: 500;
  $font-weight-bold: 600;
  $font-weight-black: 900;

  .login {
    width: 100%;

    // ===== 标题动画样式 =====
    h2 {
      background: $primary-gradient;
      // background:var(--color-primary);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      position: relative;
      letter-spacing: 0.1em;
    }

    // ===== 表单容器增强 =====
    :deep(.form) {
      padding: 1.5rem 0;
      width: 80%;

      // 输入框组 - 现代卡片式设计
      .input-group {
        position: relative;
        margin-top: 1.5rem;
        background: #fafbfc;
        border: 2px solid #e8ecf2 !important;

        border-radius: 54px;
        padding: 20px 54px;
        transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);

        &:focus {
          outline: none;
        }

        // 悬浮效果
        &:hover {
          border-color: rgba($accent-color, 0.4);
          background: #fff;
          box-shadow: 0 4px 16px rgba($accent-color, 0.08);
        }

        // 聚焦效果
        &:focus-within {
          border-color: $accent-color !important;
          background: #fff;
          box-shadow:
            0 0 0 4px rgba($accent-color, 0.1),
            0 6px 20px rgba($accent-color, 0.12);

          .lebal-tilte {
            color: $accent-color !important;
            font-weight: 600;
          }

          .svg {
            opacity: 1;
            color: $accent-color;
          }
        }

        // 输入框本身
        .main-input {
          border: none !important;
          border-bottom: none !important;
          background: transparent !important;
          padding: 0.5rem 0.5rem 0.5rem 0.25rem;

          font-size: 1rem;
          font-weight: 500;
          color: #2d3748;
          width: 100%;
          transition: all 0.3s ease;

          &:focus {
            outline: none;
            //   padding: 1.1rem 2.5rem 0.5rem 0.75rem;
          }

          &::placeholder {
            color: #8a94a6;
          }
        }

        // 浮动标签
        .lebal-tilte {
          position: absolute;
          left: 0.85rem;
          top: 50%;
          transform: translateY(-50%);
          font-size: 0.95rem;
          color: #9ca3af;
          font-weight: 500;
          pointer-events: none;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          background: transparent;
          padding: 0 0.25rem;
        }

        // 标签上浮状态
        .main-input:focus ~ .lebal-tilte,
        .main-input:valid ~ .lebal-tilte {
          font-size: 0.72rem;
          color: $accent-color;
          letter-spacing: 0.03em;
        }

        // 右侧图标
        .svg {
          position: absolute;
          right: 0.9rem;
          top: 45%;
          transform: translateY(-50%);
          font-size: 1.2rem;
          // color: #b0b8c4;
          color:var(--color-primary);
          opacity: 0.7;
          transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
        }

        // 隐藏原有的highlight-span
        .highlight-span {
          display: none;
        }

        // 验证码按钮样式
        .Verify-Btn {
          position: absolute;
          right: 6px;
          top: 50%;
          display: flex;
          justify-content: center;
          align-items: center;
          transform: translateY(-50%);
          background: $primary-gradient;
          color: #fff;
          border: none;
          width: 30%;
          min-height: 2.5rem;
          border-radius: 60px;
          font-size: 24px;
          height: 60%;
          font-size: 0.8rem;
          font-weight: 700;
          letter-spacing: 0.02em;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 2px 6px rgba(57, 140, 254, 0.25);
          white-space: nowrap;

          &:hover:not(.disabled) {
            box-shadow: 0 3px 10px rgba(57, 140, 254, 0.35);
          }

          &:active:not(.disabled) {
            transform: translateY(-50%) scale(0.96);
          }

          // 禁用状态
          &.disabled {
            background: #ccc;
            cursor: not-allowed;
            box-shadow: none;
          }
        }
      }

      // 第二个输入框间距调整
      .input-group + .input-group,
      .input-group[style*='margin-top'] {
        margin-top: 1.25rem !important;
      }

      // 协议勾选区域
      .group-password {
        margin-top: 1.5rem;
        padding: 0 10px;

        .agree-checkbox {
          display: flex;
          align-items: flex-start;
          cursor: pointer;
          user-select: none;

          input[type='checkbox'] {
            display: none;
          }

          .checkmark {
            width: 24px;
            height: 24px;
            min-width: 24px;
            border: 2px solid #d1d5db;
            border-radius: 4px;
            margin-right: 8px;
            margin-top: 2px;
            position: relative;
            transition: all 0.3s ease;
            background: #fff;

            &::after {
              content: '✓';
              position: absolute;
              top: 50%;
              left: 50%;
              font-size: 12px;
              font-weight: bold;
              color: #fff;
              transform: translate(-50%, -50%) scale(0);
              transition: transform 0.2s ease;
            }
          }

          input[type='checkbox']:checked + .checkmark {
            border-color: $accent-color;
            background: $accent-color;

            &::after {
              transform: translate(-50%, -50%) scale(1);
            }
          }

          .label-text {
            font-size: 0.85rem;
            color: $text-secondary;
            line-height: 1.5;

            .link {
              color: $accent-color;
              font-weight: 600;
              cursor: pointer;
            }
          }
        }
      }

      // 注册按钮增强
      .Signup-Btn {
        width: 100%;
        margin-top: 1.75rem !important;
        background: $primary-gradient;
        border: none;
        padding: 0 1.5rem;
        height: 88px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        min-height: 88px;
        font-size: 1rem;
        font-weight: 600;
        letter-spacing: 0.15em;
        box-shadow: 0 4px 16px rgba(57, 140, 254, 0.3);
        transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        position: relative;
        overflow: hidden;
        border-radius: 44px;

        &::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
          transition: left 0.5s ease;
        }

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(57, 140, 254, 0.45);

          &::before {
            left: 100%;
          }
        }

        &:active {
          transform: translateY(0) scale(0.98);
          box-shadow: 0 2px 10px rgba(57, 140, 254, 0.3);
        }
      }
    }
  }
</style>
